import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiLogOut, FiShield, FiMail, FiPhone, FiBriefcase, FiAward, FiCamera, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getCertifications, checkCertExpirations, uploadProfilePhoto, deleteProfilePhoto } from '../services/api';
import { CareerBadgeList } from '../components/CareerBadge';
import MyDigitalCard from '../components/MyDigitalCard';
import ConnectMyEmail from '../components/ConnectMyEmail';
import MyLicenseCredentials from '../components/MyLicenseCredentials';
import BmpLibrary from '../components/BmpLibrary';
import { API_BASE_URL } from '../config';

function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [certs, setCerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadCerts = async () => {
      try {
        await checkCertExpirations();
        const res = await getCertifications(user?.id);
        setCerts(res.data);
      } catch (err) {
        console.error('Error loading certifications:', err);
      }
    };
    if (user) loadCerts();
  }, [user]);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadProfilePhoto(user.id, file);
      await refreshUser();
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert('Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      await deleteProfilePhoto(user.id);
      await refreshUser();
    } catch (err) {
      console.error('Error removing photo:', err);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
      navigate('/login');
    }
  };

  const getCertStatusColor = (status) => {
    if (status === 'Active') return { bg: '#e8f5e9', color: '#2e7d32' };
    if (status === 'Expiring Soon') return { bg: '#fff3e0', color: '#e65100' };
    return { bg: '#ffebee', color: '#c62828' };
  };

  if (!user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Please log in to view your profile.</p>
      </div>
    );
  }

  const isAdmin = user.can_manage_payroll;

  return (
    <div style={{ maxWidth: '500px', margin: '0 auto' }}>
      {/* Profile Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '25px',
        marginBottom: '15px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        {/* Avatar with Photo Upload */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handlePhotoSelect}
          accept="image/*"
          style={{ display: 'none' }}
        />
        <div style={{ position: 'relative', display: 'inline-block', margin: '0 auto 15px' }}>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              background: user.profile_photo ? 'none' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '30px',
              fontWeight: '600',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
              border: user.profile_photo ? '3px solid #667eea' : 'none'
            }}
          >
            {user.profile_photo ? (
              <img
                src={`${API_BASE_URL}${user.profile_photo}`}
                alt={user.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              user.name?.split(' ').map(n => n[0]).join('') || <FiUser />
            )}
            {/* Camera overlay */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '28px',
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FiCamera size={14} color="white" />
            </div>
          </div>
          {uploading && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#333'
            }}>
              Uploading...
            </div>
          )}
        </div>
        {user.profile_photo && (
          <button
            onClick={handleRemovePhoto}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              margin: '0 auto 10px',
              background: 'none',
              border: 'none',
              color: '#999',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            <FiTrash2 size={12} /> Remove photo
          </button>
        )}

        <h2 style={{ margin: '0 0 5px', fontSize: '22px' }}>{user.name}</h2>
        
        {user.role && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            backgroundColor: isAdmin ? '#e3f2fd' : '#f5f5f5',
            color: isAdmin ? '#1976d2' : '#666',
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '14px',
            marginTop: '5px'
          }}>
            {isAdmin && <FiShield size={14} />}
            {user.role}
          </div>
        )}

        {/* Career Track Badges */}
        {user.career_tracks && user.career_tracks.length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
            <CareerBadgeList tracks={user.career_tracks} size="large" />
          </div>
        )}
      </div>

      {/* Connect your company mailbox so client email lands on jobs */}
      <ConnectMyEmail />

      {/* Digital business card — share via QR, link, text, or email */}
      <MyDigitalCard />

      {/* Contact Info */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '15px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 15px', fontSize: '16px', color: '#666' }}>Contact Information</h3>
        
        {user.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: '1px solid #eee' }}>
            <FiMail size={20} color="#666" />
            <div>
              <div style={{ fontSize: '12px', color: '#999' }}>Email</div>
              <div style={{ fontSize: '15px' }}>{user.email}</div>
            </div>
          </div>
        )}
        
        {user.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: '1px solid #eee' }}>
            <FiPhone size={20} color="#666" />
            <div>
              <div style={{ fontSize: '12px', color: '#999' }}>Phone</div>
              <div style={{ fontSize: '15px' }}>{user.phone}</div>
            </div>
          </div>
        )}

        {user.username && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
            <FiBriefcase size={20} color="#666" />
            <div>
              <div style={{ fontSize: '12px', color: '#999' }}>Username</div>
              <div style={{ fontSize: '15px' }}>{user.username}</div>
            </div>
          </div>
        )}
      </div>

      {/* Permissions Info */}
      {isAdmin && (
        <div style={{
          backgroundColor: '#e3f2fd',
          borderRadius: '16px',
          padding: '15px 20px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FiShield size={24} color="#1976d2" />
          <div>
            <div style={{ fontWeight: '600', color: '#1976d2' }}>Admin Access</div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              You can approve timesheets and manage team
            </div>
          </div>
        </div>
      )}

      {/* Professional seal & license — printed on sealed letters */}
      <MyLicenseCredentials />

      {/* BMP codes used by the photo markup tool */}
      <BmpLibrary />

      {/* My Certifications */}
      {certs.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '15px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 15px', fontSize: '16px', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiAward size={18} /> My Certifications
          </h3>
          {certs.map(c => {
            const statusStyle = getCertStatusColor(c.status);
            return (
              <div key={c.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid #eee'
              }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '500' }}>{c.name}</div>
                  {c.issuing_body && <div style={{ fontSize: '12px', color: '#999' }}>{c.issuing_body}</div>}
                  {c.expiration_date && (
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                      Expires: {new Date(c.expiration_date + 'T00:00:00').toLocaleDateString()}
                    </div>
                  )}
                </div>
                <span style={{
                  padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                  backgroundColor: statusStyle.bg, color: statusStyle.color
                }}>
                  {c.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        style={{
          width: '100%',
          padding: '16px',
          backgroundColor: '#f44336',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}
      >
        <FiLogOut size={20} /> Log Out
      </button>

      {/* App Version */}
      <div style={{ textAlign: 'center', marginTop: '20px', color: '#999', fontSize: '12px' }}>
        SurveyPro v1.0.0
      </div>
    </div>
  );
}

export default Profile;